import type { Coworker } from 'types/coworker';
import type { OrgData, PAData } from 'types/dataContext';
import { DateHelper, INDEFINITE_END_DATE } from 'utils/date';
import { sortBy } from 'utils/sorting';
import { isNullOrUndefined } from 'utils/validation';
import type { Scenario } from 'types/scenario';
/**
 * Utility function to filter out null | undefined | '' values from an array.
 * @param OrganizationData - The array to filter of any type T.
 * @returns A new array with all falsy values include '', null, undefined are removed.
 */
const filterInvalidData = <T extends object>(
    OrganizationData: T[],
    keys: (keyof T)[]
): T[] => OrganizationData.filter(obj => keys.every(key => !isNullOrUndefined(obj[key]) && obj[key] !== ''));

/**
 * Sorts the organization data by name in Ascending | Descending order.
 * @param data - The array of organization data to sort.
 * @param key - The key of the object to sort by, which should be a string or number.
 * @returns A new array sorted by the specified key in ascending order.
 * Using this due to OPP OM data sends inconsistent data structures for Japan.
 */
export const sortOrgData = <T extends Record<string, unknown>, TKey extends keyof T>(
    data: T[],
    key: T[TKey] extends string | number ? TKey : never): T[] => data?.sort(sortBy(key, 'ASC'));

/**
 * Transforms the organization data to ensure consistent structure.
 * @param orgData - The organization data object containing businessUnits, divisions, departments, costCentres, and subDepartments.
 * @returns A new organization data object with consistent structure.
 */
export const orgDataTransformation = (orgData: OrgData) => ({
    ...orgData,
    businessUnits: sortOrgData(filterInvalidData(orgData.businessUnits, ['name']), 'name'),
    divisions: sortOrgData(filterInvalidData(orgData.divisions, ['name', 'code']), 'name'),
    // departments: sortOrgData(orgData.departments, 'name'), --- IGNORE --- Leaving out departments due to not showing disabled in UI
    costCentres: sortOrgData(filterInvalidData(orgData.costCentres, ['name']), 'name'),
    // subDepartments: sortOrgData(orgData.subDepartments, 'name') --- IGNORE --- Leaving out subDepartments due to not showing disabled in UI
});

const updateContractEndDate = (paCoworker: Coworker) => {
    const coworkerData = { ...paCoworker };
    // If coworker contract enddate is supposed to be null but BE sends year 9999 convert it to null
    if (coworkerData.contractEndDate === INDEFINITE_END_DATE) {
        coworkerData.contractEndDate = null;
    }

    return coworkerData;
};

/**
 * Returns the oldest (earliest) non-empty date from the provided values. Null, undefined, and empty-string values are ignored.
 * @param dates - Date candidates in YYYY-MM-DD format.
 * @returns The earliest valid date, or null when no valid dates are provided.
 * @example getOldestAvailableDate('2027-08-29', null, '2026-08-31') => '2026-08-31'
 */
const getOldestAvailableDate = (...dates: Array<string | null | undefined>) => {
    const validDates = dates.filter((date): date is string => !isNullOrUndefined(date) && date !== '');

    return validDates.length > 0 ? DateHelper.getEarliestDate(validDates) : null;
};

/**
 * Returns the latest non-empty date from the provided values. Null, undefined, and empty-string values are ignored.
 * @param dates - Date candidates in YYYY-MM-DD format.
 * @returns The latest valid date, or null when no valid dates are provided.
 * @example getLatestAvailableDate('2000-01-01', '2025-01-01') => '2025-01-01'
 */
const getLatestAvailableDate = (...dates: Array<string | null | undefined>) => {
    const validDates = dates.filter((date): date is string => !isNullOrUndefined(date) && date !== '');

    return validDates.length > 0 ? DateHelper.getLatestDate(validDates) : null;
};

/* TODO: This func is needed for Finland data where costDistribution might be missing home cost centre
    ! * check with OPP PA data for finland when the data is ready enable if needed
const updateCostDistribution = (coworker: Coworker) => {
    const cwClone = { ...coworker };
    /*
        Some coworker do not have their home cost centre represented in the costDistribution.
        Adding it at 0 percent if it is missing to make sure it shows up in the grid, since it's used for contract mix calculations.

    if (!coworker?.costDistributions?.some(cd => cd.costCentre === coworker?.costCentre)) {
        cwClone.costDistributions.push({
            costCentre: coworker?.costCentre,
            costCentrePercent: 0,
            isPrimary: false,
        });
    }

    return cwClone;
};
*/

/**
* Updates a coworker's attributes by mapping and enriching data from organization data.
* Adds department code/name, division code/name, personal area name, cost centre information,
* @param paCoworker - The coworker object from PA data.
* @param orgData - The organization data containing business units, divisions, and departments.
* @returns A new coworker object with additional mapped attributes.
*/
const updateAttributes = (paCoworker: Coworker, orgData: OrgData | undefined) => {
    const cwDepartment = orgData?.departments.find(({ id }) => id === paCoworker?.orgId);
    const cwDivision = orgData?.divisions.find(({ id }) => id === cwDepartment?.parentId);
    const primaryCostCentre = paCoworker.cost.find(cd => cd.isPrimary);
    const employeeContribution = paCoworker.contributions.find(cd => cd.employeeId);
    const isFranceCoworker = orgData?.country === 'FR';

    return {
        ...paCoworker,
        departmentCode: cwDepartment?.code || '',
        departmentName: cwDepartment?.name || '',
        personalAreaName: orgData?.businessUnits.find(({ id }) => id === paCoworker.orgUnitId)?.name || '',
        division: cwDivision?.code || '',
        divisionName: cwDivision?.name || '',
        costCentre: primaryCostCentre?.id || '',
        costDistributions: paCoworker.cost.map(item => ({
            costCentre: item.id,
            costCentrePercent: item.percentage,
            isPrimary: item.isPrimary
        })),
        contractStartDate: getLatestAvailableDate(paCoworker.contractStartDate, DateHelper.getEarliestDate(paCoworker.validity)), // use the latest date
        contractEndDate: getOldestAvailableDate(
            paCoworker.contractEndDate,
            paCoworker.terminationDate,
            DateHelper.getLatestDate(paCoworker.validity)
        ), // earliest date
        employeeId: employeeContribution?.employeeId || '',
        legalFullName: `${paCoworker.firstName} ${paCoworker.middleName ? paCoworker.middleName : ''} ${paCoworker.lastName}`,
        isModulated: isFranceCoworker && paCoworker.flags.some(flag => flag.subType === 'FR01') // flag is set for france coworkers with active modulation.
    };
};

// generic func to update and safecheck the PAData in state.
export const updatePAData = (paDataResponse: PAData, orgData: OrgData | undefined) => paDataResponse?.coworkers?.map(paCoworker => {
    const coworkerData = updateContractEndDate(paCoworker);
    const manuallyAddedData = updateAttributes(coworkerData, orgData);
    // const cwClone = updateCostDistribution(manuallyAddedData);

    return manuallyAddedData;
});

/**
 * Updates dummy coworker scenario attributes to validity format to handle in the grid and calculations.
 * @param scenario - The scenario dummy coworker object containing coworkers with contract start and end dates.
 * @returns A new scenario object with updated coworker attributes to include validity format.
 */
export const updateScenarioAttributes = (scenario: Scenario): Scenario => ({
    ...scenario,
    scenarioData: scenario.scenarioData.map(cw => {
        if (!cw.dummyCoworker) {
            return cw;
        }

        return {
            ...cw,
            dummyCoworker: {
                ...cw.dummyCoworker,
            },
        };
    }),
});
